import React, { useEffect, useState, useCallback } from "react";
import {
  ShoppingCart, Layers, Sprout, Warehouse, Truck, PackageCheck, Receipt,
  Check, AlertTriangle, Clock, RefreshCw, Unlock, Radio, CheckCircle2,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, Modal } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const PHASE_ICON = {
  branch_orders: ShoppingCart, consolidation: Layers, fresh_market: Sprout,
  warehouse: Warehouse, delivery: Truck, receiving: PackageCheck, pos: Receipt,
};

const STATUS = {
  COMPLETED: { dot: "bg-success text-white", ring: "border-success bg-success-light text-success-text", text: "text-success-text", label: "Completed" },
  IN_PROGRESS: { dot: "bg-primary text-white", ring: "border-primary bg-primary-light text-primary", text: "text-primary", label: "In Progress" },
  WAITING: { dot: "bg-stone-200 text-stone-400", ring: "border-line bg-app text-ink-mute", text: "text-ink-mute", label: "Waiting" },
  DELAYED: { dot: "bg-danger text-white", ring: "border-danger bg-danger-light text-danger-text", text: "text-danger-text", label: "Delayed" },
};

function CheckpointDot({ status, t }) {
  const s = STATUS[status] || STATUS.WAITING;
  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`flex h-6 w-6 items-center justify-center rounded-full ${s.dot} ${status === "IN_PROGRESS" ? "animate-pulse" : ""}`}>
        {status === "COMPLETED" ? <Check size={14} strokeWidth={3} />
          : status === "DELAYED" ? <AlertTriangle size={12} />
          : status === "IN_PROGRESS" ? <Radio size={12} />
          : <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />}
      </span>
    </div>
  );
}

export default function ControlTower({ embedded = false }) {
  const { t } = useT();
  const toast = useToast();
  const { isRole } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reopenFor, setReopenFor] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const canReopen = isRole("SUPER_ADMIN", "OWNER", "CENTRAL_PURCHASING");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ops-cycle/today");
      setData(data); setDenied(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const submitReopen = async () => {
    if (!reason.trim()) { toast.error(t("Reason is required")); return; }
    setSaving(true);
    try {
      await api.post("/ops-cycle/reopen", { branch_id: reopenFor.branch_id, reason: reason.trim() });
      toast.success(`${reopenFor.branch_name} ${t("reopened")}`);
      setReopenFor(null); setReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setSaving(false); }
  };

  if (denied) {
    return (
      <Card className="p-6 text-center text-sm text-ink-mute" data-testid="control-tower-denied">
        {t("The Control Tower is available to HQ and Owner accounts only.")}
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="p-6 text-center text-sm text-ink-mute" data-testid="control-tower-loading">
        {t("Loading operations pipeline...")}
      </Card>
    );
  }

  return (
    <div data-testid="control-tower" className={embedded ? "" : "animate-fade-up"}>
      {/* Header strip */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-3xl border border-orange-100 bg-gradient-to-r from-orange-50 via-amber-50 to-white p-4 shadow-card">
        <div className="flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary text-white"><Radio size={20} /></span>
          <div>
            <p className="font-heading text-lg font-extrabold leading-tight text-ink">{t("Operations Control Tower")}</p>
            <p className="text-xs text-ink-soft">{t("Today's operational cycle")} · {data.cycle_date}</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Badge tone={data.past_cutoff ? "danger" : "info"} data-testid="ct-cutoff">
            <Clock size={12} /> {t("Cutoff")} {data.cutoff_time} {data.past_cutoff ? `· ${t("Closed")}` : `· ${t("Open")}`}
          </Badge>
          <Badge tone="success" data-testid="ct-completed"><CheckCircle2 size={12} /> {data.completed_count}/{data.branch_count} {t("done")}</Badge>
          {data.late_count > 0 && <Badge tone="danger" data-testid="ct-late"><AlertTriangle size={12} /> {data.late_count} {t("late")}</Badge>}
          <button onClick={load} data-testid="ct-refresh" className="rounded-xl border border-line bg-white p-2 text-ink-soft transition hover:text-primary">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Blocking banner */}
      {data.blocking && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid="ct-blocking">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-warn-text" />
          <p className="text-sm text-ink-soft">
            {t("Bottleneck")}: <b className="text-ink">{t(data.blocking.label)}</b>
            {data.blocking.branches?.length ? ` — ${t("waiting on")}: ${data.blocking.branches.join(", ")}` : ""}
          </p>
        </div>
      )}

      {/* Global 7-phase pipeline */}
      <Card className="mb-5 overflow-x-auto p-5" data-testid="ct-pipeline">
        <div className="flex min-w-[680px] items-start">
          {data.phases.map((ph, i) => {
            const Icon = PHASE_ICON[ph.key] || Layers;
            const s = STATUS[ph.status] || STATUS.WAITING;
            return (
              <React.Fragment key={ph.key}>
                <div className="flex flex-1 flex-col items-center text-center" data-testid={`ct-phase-${ph.key}`}>
                  <span className={`flex h-12 w-12 items-center justify-center rounded-2xl border-2 ${s.ring} ${ph.status === "IN_PROGRESS" ? "animate-pulse" : ""}`}>
                    <Icon size={22} />
                  </span>
                  <span className="mt-1 text-[10px] font-bold text-stone-400">{i + 1}</span>
                  <p className="mt-0.5 px-1 text-xs font-bold leading-tight text-ink">{t(ph.label)}</p>
                  <Badge tone={ph.status === "COMPLETED" ? "success" : ph.status === "DELAYED" ? "danger" : ph.status === "IN_PROGRESS" ? "primary" : "mute"} className="mt-1">
                    {ph.counts.completed}/{ph.counts.total}
                  </Badge>
                </div>
                {i < data.phases.length - 1 && (
                  <div className="mt-6 h-0.5 w-6 shrink-0 rounded bg-line sm:w-10" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Card>

      {/* Per-branch progress grid */}
      <Card className="overflow-hidden p-0" data-testid="ct-branch-grid">
        <div className="flex items-center gap-2 border-b border-line px-5 py-4">
          <Layers size={18} className="text-primary" />
          <h3 className="font-heading text-lg font-bold">{t("Branch Progress")}</h3>
          <span className="ml-auto hidden text-xs text-ink-mute sm:inline">{t("Each branch across the 7 phases")}</span>
        </div>
        <div className="divide-y divide-line">
          {data.branches.map((b) => (
            <div key={b.branch_id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center" data-testid={`ct-branch-${b.branch_name}`}>
              <div className="flex min-w-0 items-center gap-2 sm:w-44">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-app text-primary">
                  {b.logo ? <img src={b.logo} alt="" className="h-full w-full object-cover" /> : <Warehouse size={16} />}
                </span>
                <div className="min-w-0">
                  <p className="truncate font-heading text-sm font-extrabold text-ink">{b.branch_name}</p>
                  <div className="flex flex-wrap items-center gap-1">
                    {b.completed
                      ? <Badge tone="success">{t("Completed")}</Badge>
                      : <Badge tone={b.delayed ? "danger" : "primary"}>{t(b.current_phase_label)}</Badge>}
                    {b.late && <Badge tone="danger" data-testid={`ct-late-${b.branch_name}`}>{t("Late")}</Badge>}
                    {b.reopened && <Badge tone="info"><Unlock size={11} /> {t("Reopened")}</Badge>}
                  </div>
                </div>
              </div>

              <div className="flex flex-1 items-center justify-between gap-1 overflow-x-auto">
                {b.checkpoints.map((c) => (
                  <div key={c.key} className="flex flex-col items-center gap-1" title={`${t(c.label)} · ${t((STATUS[c.status] || STATUS.WAITING).label)}`} data-testid={`ct-cp-${b.branch_name}-${c.key}`}>
                    <CheckpointDot status={c.status} t={t} />
                  </div>
                ))}
              </div>

              {canReopen && (b.locked || b.late) && !b.reopened && (
                <Button size="sm" variant="outline" onClick={() => { setReopenFor(b); setReason(""); }} data-testid={`ct-reopen-${b.branch_name}`}>
                  <Unlock size={14} /> {t("Reopen")}
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Modal open={!!reopenFor} onClose={() => setReopenFor(null)} title={`${t("Reopen Order")} · ${reopenFor?.branch_name || ""}`}
        footer={<>
          <Button variant="ghost" onClick={() => setReopenFor(null)}>{t("Cancel")}</Button>
          <Button onClick={submitReopen} loading={saving} data-testid="reopen-confirm">{t("Reopen")}</Button>
        </>}>
        <p className="mb-3 text-sm text-ink-soft">{t("This branch missed the night cutoff. Reopening is audited.")}</p>
        <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Reason")}</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="reopen-reason"
          className="w-full rounded-xl border border-line p-3 text-sm focus:border-primary focus:outline-none"
          placeholder={t("e.g. Branch had a network outage")} />
      </Modal>
    </div>
  );
}
